/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import {
    Button,
    EmailField,
    Heading,
    TextField,
    Icon,
    PhoneNumberField,
    DividerNew,
    Checkbox,
    FormValidationAlert,
    type FormValidationAlertErrors,
    type TextFieldValidator,
} from 'sarsaparilla';
import { useFieldRefValidation, getMessageFromError } from 'sarsaparilla/src/utils';
import { TERMS_OF_SERVICE_URL } from 'site-kit/src/constants/kbArticles';

import {
    useRecaptchaWithFallback,
    recaptchaV2ValidationError,
    RecaptchaCheckbox,
} from '../../../recaptcha';
import { postAccountRegistration } from '../../../api/account';
import { ProtectedByRecaptcha } from '../components/ProtectedByRecaptcha';
import { useAuthModalsContext } from '../AuthModalContext';
import { CreateAccountErrors } from './CreateAccountErrors';

interface CreateAccountFormProps {
    goToEmailVerification: (email: string) => void;
}

/** Maximum number of characters allowed in the first and last name fields. */
const NAME_MAX_LENGTH = 50;

/**
 * Builds a validator that rejects values exceeding {@link NAME_MAX_LENGTH}.
 * Leading/trailing whitespace is ignored so the check matches the trimmed
 * value that is ultimately submitted.
 *
 * @param fieldLabel - Human-readable label used in the error message.
 * @returns A Sarsaparilla {@link TextFieldValidator}.
 */
const createNameLengthValidator =
    (fieldLabel: string): TextFieldValidator =>
    (value: string) => {
        if (value.trim().length > NAME_MAX_LENGTH) {
            return {
                isValid: false,
                formattedValue: null,
                errorText: `${fieldLabel} must be ${NAME_MAX_LENGTH} characters or fewer.`,
            };
        }

        return { isValid: true, formattedValue: null, errorText: null };
    };

const validateFirstName = createNameLengthValidator('First Name');
const validateLastName = createNameLengthValidator('Last Name');

export function CreateAccountForm({ goToEmailVerification }: CreateAccountFormProps) {
    const [isLoading, setIsLoading] = React.useState(false);
    const [errorType, setErrorType] = React.useState<
        | 'invalidCellPhone'
        | 'additionalChallenge'
        | 'recaptchaFailed'
        | 'existingAccount'
        | 'unknown'
        | null
    >(null);
    const [validationErrors, setValidationErrors] =
        React.useState<FormValidationAlertErrors | null>(null);

    const { setScreen } = useAuthModalsContext();
    const { addFieldRef, validate } = useFieldRefValidation();
    const {
        getToken,
        getRecaptchaRegion,
        handleRecaptchaError,
        isV2Invalid,
        recaptchaCheckboxProps,
    } = useRecaptchaWithFallback();

    const submitButtonText = 'Create Account';

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true);
        setValidationErrors(null);
        setErrorType(null);

        // Get form values and validate
        const formData = new FormData(event.currentTarget);
        const email = formData.get('email')?.toString().trim().toLowerCase();
        const firstName = formData.get('firstName')?.toString().trim();
        const lastName = formData.get('lastName')?.toString().trim();
        const cellPhone = formData.get('cellPhone')?.toString().trim();
        const optIn = formData.has('optIn');

        const { isValid, errors } = validate();

        if (!isValid || !email || !firstName || !lastName || !cellPhone || isV2Invalid) {
            if (isV2Invalid) {
                errors.push(recaptchaV2ValidationError);
            }
            setValidationErrors(errors);
            setIsLoading(false);
            return;
        }

        try {
            // Get Recaptcha type and token
            const recaptchaToken = await getToken('initializeRegistration');
            const recaptchaRegion = getRecaptchaRegion();

            // Extra check to make sure we have a token value
            if (!recaptchaToken) {
                setErrorType('recaptchaFailed');
                setIsLoading(false);
                return;
            }

            const { data } = await postAccountRegistration({
                email,
                firstName,
                lastName,
                cellPhone,
                optIn,
                recaptchaToken,
                recaptchaRegion,
            });

            if (data.success) {
                goToEmailVerification(email);
            } else {
                setErrorType('unknown');
            }
        } catch (error) {
            const { shouldRetry, isFailed } = handleRecaptchaError(error);
            const messageFromError = getMessageFromError(error);

            // Check for recaptcha-specific errors
            if (shouldRetry) {
                setErrorType('additionalChallenge');
            } else if (isFailed) {
                setErrorType('recaptchaFailed');
            } else if (messageFromError === 'invalid cellphone') {
                setErrorType('invalidCellPhone');
            } else if (
                messageFromError === 'An account already exists for this email address'
            ) {
                setErrorType('existingAccount');
            } else {
                setErrorType('unknown');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="px-sm-3 pt-sm-3 pb-sm-1">
            <Heading headingLevel={1} appearance="h4" className="mb-1" id="modal-heading">
                Create a New Account
            </Heading>

            <div className="pt-half">
                Already have a {process.env.SITE_NAME} account?{' '}
                <Button
                    appearance="link"
                    iconAfterElement={<Icon iconName="chevron-right" />}
                    className="ml-half"
                    style={{ verticalAlign: 'baseline' }}
                    onClick={() => {
                        setScreen('login');
                    }}
                >
                    Log In
                </Button>
            </div>

            <form className="mt-5" onSubmit={onSubmit}>
                <FormValidationAlert errors={validationErrors} className="mb-4" />

                <CreateAccountErrors errorType={errorType} className="mb-4" />

                <TextField
                    label="First Name"
                    id="firstName"
                    name="firstName"
                    isRequired
                    maxLength={NAME_MAX_LENGTH}
                    validator={validateFirstName}
                    ref={addFieldRef(0)}
                />
                <TextField
                    label="Last Name"
                    id="lastName"
                    name="lastName"
                    isRequired
                    maxLength={NAME_MAX_LENGTH}
                    className="mt-2"
                    validator={validateLastName}
                    ref={addFieldRef(1)}
                />
                <EmailField
                    label="Email"
                    id="email"
                    name="email"
                    isRequired
                    className="mt-2"
                    ref={addFieldRef(2)}
                />
                <PhoneNumberField
                    label="Mobile Phone Number"
                    id="cellPhone"
                    name="cellPhone"
                    isRequired
                    className="mt-2"
                    ref={addFieldRef(3)}
                />
                <Checkbox
                    label="Yes, email me stories that will inspire my next adventure"
                    id="optIn"
                    name="optIn"
                    className="mt-3"
                />

                <RecaptchaCheckbox
                    {...recaptchaCheckboxProps}
                    isFallbackOnly
                    action="initializeRegistration"
                    className="mt-3"
                />

                <div className="text-sm mt-5">
                    By selecting{' '}
                    <strong className="font-weight-semibold">{submitButtonText}</strong>,
                    I accept {process.env.SITE_NAME}‘s{' '}
                    <a href={TERMS_OF_SERVICE_URL}>Terms of Service</a> and{' '}
                    <a href={`${process.env.SITE_ROOT}/privacy-policy`}>Privacy Policy</a>
                    .
                </div>
                <Button
                    type="submit"
                    size="lg"
                    shouldFitContainer
                    className="mt-2"
                    isLoading={isLoading}
                    gaTrackingId="440238972040"
                >
                    {submitButtonText}
                </Button>
            </form>

            <DividerNew className="mt-5 mb-2" />

            <ProtectedByRecaptcha />
        </div>
    );
}
